import { FGRecommendationFetcher } from "@fgtheme/fg-recommendations";
import { morphSection } from '@theme/section-renderer';

/**
 * @typedef WICartUpsellComponentConfig
 * @type {{ 
 *  numberOfRecs: number,
 *  maxRecRequests: number
 * }}
 */

/**
 * @typedef WICartUpsellProductData
 * @type {{ 
 *  cartProductIds: string[],
 *  cartVariantIds: string[]
 * }}
 */

class WICartUpsell extends FGRecommendationFetcher {
  static observedAttributes = ['data-product-data'];

  strikethroughEnabled = false;
  /**
   * @type {string | undefined | null}
   */
  strikethroughPrice = null;

  /**
   * @type {any | null}
   */
  containerTplEl = null;

  /**
   * @type {any | null}
   */
  cardTplEl = null;

  /**
   * @type {import("@fgtheme/fg-recommendations").ProductVariantGraphQL["data"]["nodes"]}
   */
  recommendations = [];

  /**
   * @type {any | null}
   */
  swiper = null;

  /**
   * @type {WICartUpsellComponentConfig}
   */
  componentConfig = {
    numberOfRecs: 2,
    maxRecRequests: 2
  };

  /**
   * @type {WICartUpsellProductData}
   */
  productData = {
    cartProductIds: [],
    cartVariantIds: []
  }

  stateChanging = false;

  constructor() {
    super();
    this.initConfig();
  }

  connectedCallback() {
    this.handleStateChanged = this.handleStateChanged.bind(this);
    document.addEventListener('fgw_state_change', this.handleStateChanged);
  }

  disconnectedCallback() {
    document.removeEventListener('fgw_state_change', this.handleStateChanged);
  }

  /**
   * 
   * @param {string} name 
   * @param {*} oldValue 
   * @param {*} newValue 
   */
  attributeChangedCallback(name, oldValue, newValue) {
    /**
     * @type {{
     *   cartProductIds: string[],
     *   cartVariantIds: string[]
     * }}
     */
    const config = {
      cartProductIds: [],
      cartVariantIds: []
    }

    try {
      const productData = JSON.parse(newValue);
      const productIds = productData[0] === '' ? [] : productData[0].split(',');
      const variantIds = productData[1] === '' ? [] : productData[1].split(',');
      config.cartProductIds = productIds;
      config.cartVariantIds = variantIds;
    } catch (e) {
      console.error('Failed to parse JSON for product data attribute', e);
      return;
    }

    this.shuffleArray(config.cartProductIds);
    this.productData = config;
    const limitedProductIds = config.cartProductIds.slice(0, this.componentConfig.maxRecRequests);
    this.loadRecommendations(limitedProductIds);
  }

  initConfig() {
    this.strikethroughEnabled = this.dataset.strikethroughEnabled === "true";
    this.strikethroughPrice = this.dataset.strikethroughPrice;

    const componentConfigEl = this.querySelector('[data-config="configuration"]');
    if (!componentConfigEl) {
      console.error('No component config found');
      return;
    }

    let componentConfig = null;
    try {
      componentConfig = JSON.parse(componentConfigEl.textContent);
    } catch (e) {
      console.error('JSON parse fail for component config', e);
      return;
    }
    if (!componentConfig) {
      console.error('Component config is null');
      return;
    }
    this.componentConfig = { ...this.componentConfig, ...componentConfig };
  }

  /**
   * 
   * @param {any[]} array 
   */
  shuffleArray(array) {
    for (let i = array.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [array[i], array[j]] = [array[j], array[i]];
    }
  }

  /**
   * 
   * @param {any} event 
   */
  handleStateChanged(event) {
    // Guard against double firing of state change event
    if (this.stateChanging) {
      return;
    }
    this.stateChanging = true;
    this.attributeChangedCallback('data-product-data', this.dataset.productData, this.dataset.productData)
  }

  /**
   * 
   * @param {any[]} shopifyRecommendations 
   * @returns {any[]} - filtered array
   */
  filterShopifyProductRecommendations(shopifyRecommendations) {
    const fg_wh = globalThis.FGAvailabilityData.getCurrentWarehouse();

    return shopifyRecommendations.filter(item => {
      // No items of type other than "Bottle"
      if (item.type !== 'Bottle') {
        return false;
      }
      // No unavailable items
      if (!item.available) {
        return false;
      }
      // No items with a selling plan
      if (item.selling_plan_groups.length > 0) {
        return false;
      }
      // No items that require a selling plan
      if (item.requires_selling_plan) {
        return false;
      }
      // No items with multiple variants
      if (item.variants.length > 1) {
        return false;
      }
      // No hidden products
      if (item.tags.includes('hidden-product')) {
        return false;
      }
      // No sold out all items
      if (item.tags.includes('sold-out-all')) {
        return false;
      }
      // No sold out items for the current warehouse
      if (item.tags.includes(`sold-out-${fg_wh.toLowerCase()}`)) {
        return false;
      }
      // No items missing a featured image
      if (!item.featured_image || item.featured_image === '') {
        return false;
      }
      return true;
    });
  }

  getNodesQuery() {
    return `
      query ProductVariantNodes($ids: [ID!]!) {
        nodes(ids: $ids) {
          ... on ProductVariant {
            id
            title
            price {
              amount
            }
            compareAtPrice {
              amount
            }
            product {
              id
              title
              featuredImage {
                altText
                url
              }
              tags
              handle
              metafield_country: metafield(namespace: "fgw", key: "country") {
                value
              }
              metafield_states: metafield(namespace: "drinks_assure", key: "available_states") {
                value
              }
            }
          }
        }
      }
    `;
  }

  /**
   * @param {any} event
   * @param {import('@fgtheme/fg-recommendations').ProductVariantGraphQL["data"]["nodes"][0]} item 
   */
  async handleAddToCart(event, item) {
    event.preventDefault();
    event.stopPropagation();
    const atcButton = event.currentTarget;

    const itemToAdd = {
      id: item.id.replace('gid://shopify/ProductVariant/', ''),
      quantity: 1,
      properties: this.getLineItemProperties()
    }

    const requestBody = {
      items: [itemToAdd],
      sections: '',
    };
    const headerSectionId = document.querySelector('header.header-section').id.replace('shopify-section-', '');
    /** @type {Set<string>} */
    const cartItemComponentsSectionIds = new Set([headerSectionId]);
    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        cartItemComponentsSectionIds.add(item.dataset.sectionId);
      }
    });
    requestBody.sections = Array.from(cartItemComponentsSectionIds).join(',');
    atcButton.setAttribute('data-added', true);
    this.disableAddToCartButtons();
    const response = await fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(requestBody),
    });
    const result = await response.json();

    const closestScrollHint = atcButton.closest('scroll-hint');
    setTimeout(() => {
      atcButton.setAttribute('data-added', false);
      this.enableAddToCartButtons();
    }, 500);
    closestScrollHint.scrollTop = 0;
    morphSection(headerSectionId, result.sections[headerSectionId], 'hydration');
  }

  modifyRecommendations() {
    const filtered = this.recommendations.filter((/** @type {any} */ item) => {
      if (item.product.metafield_states && item.product.metafield_states.value) {
        const currentState = globalThis.FGAvailabilityData.getCurrentState();
        if (!item.product.metafield_states.value.includes(currentState)) {
          return false;
        }
      }
      return true;
    });

    this.recommendations = filtered;

    this.recommendations.forEach(item => {
      if (item.product.tags && item.product.tags.includes('no-strikethrough')) {
        return;
      }
      if (this.strikethroughEnabled && this.strikethroughPrice) {
        item.price.amount = this.strikethroughPrice;
      }
    });

    this.shuffleArray(this.recommendations);
  }

  getLineItemProperties() {
    return {
      '_fg_widget_id': 'WI-CART-UPSELL'
    }
  }

  /**
   * 
   * @param {string[]} productIds 
   */
  async loadRecommendations(productIds) {
    const recommendedVariantIds = await this.getProductRecommendationVariantIds(productIds);
    const withCartItemsRemoved = [...recommendedVariantIds].filter(variantId => !this.productData.cartVariantIds.includes(variantId));
    const cutToSize = withCartItemsRemoved.slice(0, this.componentConfig.numberOfRecs);
    this.recommendations = await this.getProductVariantData(cutToSize);
    this.modifyRecommendations();

    if (this.recommendations.length === 0) {
      console.warn('No recommendations found');
      const contentEl = this.querySelector('[data-content]');
      if (contentEl) {
        contentEl.innerHTML = "";
      }
      return;
    }

    this.renderUI();
    this.stateChanging = false;
  }

  renderUI() {
    this.containerTplEl = this.querySelector('template[data-tpl="container"]');
    if (!this.containerTplEl) {
      console.error('No container template found.');
      return;
    }

    this.cardTplEl = this.querySelector('template[data-tpl="card"]');
    if (!this.cardTplEl) {
      console.error('No product card template found');
      return;
    }

    const containerTpl = this.containerTplEl.content.cloneNode(true);

    /**
     * @type {any[]}
     */
    const cardNodes = [];
    this.recommendations.forEach(item => {
      const cardTpl = this.cardTplEl.content.cloneNode(true);
      const cardContainer = cardTpl.querySelector('[data-card-container');
      cardContainer.setAttribute('data-variant-id', item.id);

      const imageEl = cardTpl.querySelector('[data-image]');
      imageEl.setAttribute('src', item.product.featuredImage.url);
      imageEl.setAttribute('alt', item.product.featuredImage.altText);

      cardTpl.querySelector('[data-title]').textContent = item.product.title;

      const prodId = item.product.id.substring(item.product.id.lastIndexOf('/') + 1);
      const priceEl = cardTpl.querySelector('[data-price]');
      priceEl.textContent = `$${parseFloat(item.price.amount).toFixed(2)}`;
      priceEl.dataset.slAttributeP = `${prodId}`;
      if (item.compareAtPrice && item.compareAtPrice.amount) {
        const capEl = cardTpl.querySelector('[data-compare-at-price]');
        capEl.textContent = `$${parseFloat(item.compareAtPrice.amount).toFixed(2)}`;
        capEl.dataset.slAttributeCap = `${prodId}`;
      }

      cardTpl.querySelector('[data-atc-button]').addEventListener('click', (/** @type {any} */ event) => {
        this.handleAddToCart(event, item);
      });

      cardNodes.push(cardTpl);
    });

    containerTpl.querySelector('[data-cards]').append(...cardNodes);

    const contentEl = this.querySelector('[data-content]');
    if (!contentEl) {
      console.error('[data-content] element not found.');
      return;
    }

    contentEl.innerHTML = "";
    contentEl.appendChild(containerTpl);
    this.initSwiper();
  }

  initSwiper() {
    const swiperElement = this.querySelector('.swiper');
    const swiperNavPrevElement = this.querySelector('.swiper-button-prev');
    const swiperNavNextElement = this.querySelector('.swiper-button-next');
    const swiperPaginationElement = this.querySelector('.swiper-pagination');

    this.swiper = new Swiper(swiperElement, {
      slidesPerView: 1,
      loop: true,
      pagination: {
        el: swiperPaginationElement
      },
      navigation: {
        addIcons: false,
        prevEl: swiperNavPrevElement,
        nextEl: swiperNavNextElement,
      },
    });
  }

  disableAddToCartButtons() {
    const atcButtons = this.querySelectorAll('[data-atc-button]');
    atcButtons.forEach(( /** @type {any} */ button) => {
      button.disabled = true;
    });
  }

  enableAddToCartButtons() {
    const atcButtons = this.querySelectorAll('[data-atc-button]');
    atcButtons.forEach(( /** @type {any} */button) => {
      button.disabled = false;
    });
  }
}

if (!customElements.get('wi-cart-upsell')) {
  customElements.define('wi-cart-upsell', WICartUpsell);
}